import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const SESSION_KEY = "anp_chat_session";
const ACTIVITY_KEY = "anp_chat_last_activity";
// Session expires after 15 minutes of no customer response — the next open or
// message starts a fresh session (new staff name, clean conversation).
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const touchActivity = () => {
  localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
};

const isSessionExpired = () => {
  const last = Number(localStorage.getItem(ACTIVITY_KEY) || 0);
  return last > 0 && Date.now() - last > SESSION_TIMEOUT_MS;
};

// Minimal markdown renderer for chat bubbles: **bold** and line breaks.
// Also hides the internal [LEAD]...[/LEAD] block (complete or partial,
// in case it is still streaming in).
const stripLeadBlock = (text) =>
  text
    .replace(/\[LEAD\][\s\S]*?\[\/LEAD\]/g, "")
    .replace(/\[LEAD\][\s\S]*$/g, "")
    .trimEnd();

const renderText = (text) => {
  const cleaned = stripLeadBlock(text);
  const parts = cleaned.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part.split("\n").map((line, j) => (
      <span key={`${i}-${j}`}>
        {j > 0 && <br />}
        {line}
      </span>
    ));
  });
};

const ChatWidget = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [greeted, setGreeted] = useState(false);
  // DEBUG: verify intervals run in this component at all
  useEffect(() => {
    (window.__dbg = window.__dbg || []).push("widget mounted");
    const id = setInterval(() => {
      (window.__dbg = window.__dbg || []).push("mount interval tick");
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  // Ties an in-flight stream to the session it was started in, so deltas from
  // a rotated (expired) session are dropped instead of leaking into the new one.
  const streamSession = useRef(null);
  // Human-typing queue: incoming LLM deltas are buffered here and revealed at
  // a natural typing pace instead of appearing in bursts.
  const pendingRef = useRef("");
  const typingTimer = useRef(null);
  const streamingRef = useRef(false);
  // Mirror of messages for use inside async closures (state is stale there).
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Reveal buffered text at a human pace — a few chars per tick with slight
  // jitter, speeding up automatically when the backlog grows too large.
  const startTyper = useCallback(() => {
    if (typingTimer.current) return;
    typingTimer.current = setInterval(() => {
      const pending = pendingRef.current;
      if (!pending.length) {
        if (!streamingRef.current) {
          clearInterval(typingTimer.current);
          typingTimer.current = null;
        }
        return;
      }
      const backlog = pending.length;
      const chars =
        backlog > 600 ? 16 : backlog > 250 ? 8 : backlog > 80 ? 4 : 2 + Math.floor(Math.random() * 2);
      const slice = pending.slice(0, chars);
      pendingRef.current = pending.slice(chars);
      setMessages((prev) => {
        if (!prev.length) return prev;
        const copy = [...prev];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { ...last, content: last.content + slice };
        return copy;
      });
    }, 42);
  }, []);

  // Rotate to a brand-new session: new session id, polite "session ended"
  // notice, then the greeting (with a fresh staff name) is regenerated.
  const rotateSession = useCallback(() => {
    const id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(SESSION_KEY, id);
    sessionId.current = id;
    pendingRef.current = "";
    setMessages([{ role: "notice", content: t("chat.session_ended") }]);
    touchActivity();
  }, [t]);

  // Open/close the panel. When opening after 15+ idle minutes, end the old
  // session and start a fresh one so a new staff name greets the customer.
  const togglePanel = useCallback(() => {
    if (!open && isSessionExpired()) {
      rotateSession();
      setGreeted(false);
    }
    setOpen((v) => !v);
  }, [open, rotateSession]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  // Restore history on first open, or generate a greeting for a new session.
  // If the previous session expired (15 min idle), rotate to a fresh one first.
  // NOTE: after a page reload `messages` is always empty, so the expiry check
  // must rely on the stored activity timestamp alone — not React state.
  useEffect(() => {
    if (!open || greeted) return;
    if (isSessionExpired()) {
      rotateSession();
    }
    setGreeted(true);
    touchActivity();
    fetch(`${API}/chat/history/${sessionId.current}`)
      .then((r) => r.json())
      .then(async (data) => {
        if (data.messages?.length) {
          setMessages(data.messages);
        } else {
          const res = await fetch(`${API}/chat/greeting`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId.current }),
          });
          const g = await res.json();
          if (g.greeting) {
            setMessages((prev) => [...prev, { role: "assistant", content: g.greeting }]);
            touchActivity();
          }
        }
      })
      .catch(() => {});
  }, [open, greeted, rotateSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streaming, scrollToBottom]);

  const send = async () => {
    try {
      await sendInner();
    } catch (e) {
      console.error("chat send failed:", e);
      window.__chatErr = String(e && e.stack || e);
    }
  };

  const sendInner = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    // Idle > 15 min with the panel open? End the old session and start fresh.
    if (isSessionExpired() && messages.length > 0) {
      rotateSession();
    }
    touchActivity();
    setInput("");
    const streamFor = sessionId.current;
    streamSession.current = streamFor;
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    streamingRef.current = true;
    pendingRef.current = "";

    try {
      // Fire-and-poll: the preview ingress intermittently swallows POST
      // *responses* initiated from app code (the request always reaches the
      // server and the reply is persisted — only the response body never
      // arrives). So we fire the request and poll the reliable history GET
      // until the new assistant reply appears, then type it out.
      (window.__dbg = window.__dbg || []).push("sendInner try");
      const assistantBefore = messagesRef.current.filter((m) => m.role === "assistant").length;
      (window.__dbg = window.__dbg || []).push("assistantBefore=" + assistantBefore);
      fetch(`${API}/chat/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: streamFor, message: text }),
      }).then(() => (window.__dbg = window.__dbg || []).push("post resolved")).catch((e) => (window.__dbg = window.__dbg || []).push("post rejected " + e));

      const startedAt = Date.now();
      const poll = setInterval(async () => {
        (window.__dbg = window.__dbg || []).push("poll tick");        if (streamSession.current !== streamFor) {
          clearInterval(poll);
          return;
        }
        if (Date.now() - startedAt > 90000) {
          clearInterval(poll);
          pendingRef.current = "";
          setMessages((prev) => {
            if (!prev.length) return prev;
            const copy = [...prev];
            copy[copy.length - 1] = { role: "assistant", content: t("chat.error_fallback") };
            return copy;
          });
          streamingRef.current = false;
          setStreaming(false);
          return;
        }
        try {
          const r = await fetch(`${API}/chat/history/${streamFor}`);
          const d = await r.json();
          const assistants = (d.messages || []).filter((m) => m.role === "assistant");
          (window.__dbg = window.__dbg || []).push("poll got " + assistants.length);
          if (assistants.length > assistantBefore) {
            clearInterval(poll);
            const reply = assistants[assistants.length - 1].content || "";
            pendingRef.current += reply;
            streamingRef.current = false;
            startTyper();
            setStreaming(false);
          }
        } catch (e) {
          (window.__dbg = window.__dbg || []).push("poll err " + e);
        }
      }, 1200);
      (window.__dbg = window.__dbg || []).push("poll created");
    } catch {
      pendingRef.current = "";
      setMessages((prev) => {
        if (!prev.length) return prev;
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: "assistant",
          content: t("chat.error_fallback"),
        };
        return copy;
      });
      streamingRef.current = false;
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Floating launcher — bottom right (WhatsApp sits bottom-left) */}
      <button
        type="button"
        data-testid="chatbot-float-button"
        aria-label={t("chat.open_label")}
        onClick={togglePanel}
        className="fixed bottom-6 right-6 z-40 group"
      >
        <span className="absolute inset-0 rounded-full bg-gold-400/40 animate-pulse-ring pointer-events-none" />
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-forest-800 text-gold-300 shadow-2xl group-hover:bg-forest-700 transition-all duration-500 group-hover:scale-105">
          {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </span>
        <span className="absolute right-16 top-1/2 -translate-y-1/2 bg-forest-800 text-white text-xs px-3 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
          {t("chat.open_label")}
        </span>
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-40 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[70vh] flex flex-col bg-card border border-border rounded-lg shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        >
          {/* Header */}
          <div className="bg-forest-800 text-white px-5 py-4 flex items-center gap-3 shrink-0">
            <img src="/logo-mark.png" alt="PT ANP" className="h-9 w-9 rounded-full object-cover bg-white/90 ring-1 ring-white/30 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-base leading-tight truncate" data-testid="chatbot-title">
                {t("chat.title")}
              </p>
              <p className="text-[11px] text-white/70 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                {t("chat.subtitle")}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="chatbot-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-background">
            {messages.map((m, i) =>
              m.role === "notice" ? (
                <div key={i} className="flex justify-center" data-testid={`chatbot-notice-${i}`}>
                  <p className="text-[11px] text-muted-foreground italic text-center px-4 py-1.5 bg-muted/50 rounded-full max-w-[90%]">
                    {m.content}
                  </p>
                </div>
              ) : (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chatbot-message-${m.role}-${i}`}
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-forest-600 text-white rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm"
                  }`}
                >
                  {m.content === "" && m.role === "assistant" ? (
                    <span className="inline-flex gap-1 items-center text-muted-foreground" data-testid="chatbot-typing">
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  ) : (
                    renderText(m.content)
                  )}
                </div>
              </div>
              )
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border p-3 flex items-center gap-2 bg-card shrink-0">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              data-testid="chatbot-input"
              placeholder={t("chat.placeholder")}
              disabled={streaming}
              className="flex-1 bg-muted/50 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-forest-500 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={send}
              disabled={streaming || !input.trim()}
              data-testid="chatbot-send"
              aria-label={t("chat.send_label")}
              className="w-10 h-10 rounded-full bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white flex items-center justify-center transition-colors shrink-0"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 rtl-flip" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
